// WorldView: everything static in the level, built once from the sim World (same seed, same
// island, same collision). The collision rule holds: terrain is sampled from world.terrain()
// and clipped at world.edgeRadius; every box/cyl is drawn at its exact footprint and height.
//
// Draw calls (quality 2): island tops+path 1, strata 1, town 1, signs 1, wires 1, props 1,
// grass 2, sky dome 1, cloud sea 1, silhouettes 1, motes 1, gate 3 (+ shadow pass casters).

import * as THREE from 'three';
import type { ViewCtx, WorldViewApi } from '../contracts';
import { HAZE, paintedMaterial, setMood } from '../materials';
import { smoothstep } from '../../../sim/core';
import { GroundField } from './field';
import { buildIslandsSteps } from './terrain';
import { Town } from './town';
import { buildProps, buildRocksSteps, mergeAll } from './props';
import { Grass } from './grass';
import { Foliage } from './foliage';
import { Sky } from './sky';
import { Gate } from './gate';
import { Train } from './train';
import { WaterView } from './water';
import { buildLandforms, LandformRuns } from './landforms';
import { DayNight } from './daynight';
import { budget, finish, sharedTexture } from '../warm';
import { World } from '../../../sim/world';
import { StonesView, StonesFx } from './stones';
import type { RuinFx } from './town_burn';

const ASSET = '/assets/landscape/';

export class WorldView implements WorldViewApi {
  readonly group = new THREE.Group();
  field!: GroundField;
  private grass!: Grass;
  private sky!: Sky;
  private gate!: Gate;
  /** the red bonus-round doors on the sky islet (island 2 of each village only) */
  private bonusGate: Gate | null = null;
  private train: Train | null = null;
  private town!: Town;
  private stones!: StonesView;
  private foliage!: Foliage;
  private water!: WaterView;
  private mood = -1;
  private ctx: ViewCtx;
  private disposables: { dispose(): void }[] = [];

  private sunLight: THREE.DirectionalLight | null = null;
  private hemiLight: THREE.HemisphereLight | null = null;
  /** time of day (world/daynight.ts); the game feeds it the shared server clock */
  readonly dayNight = new DayNight();
  /**
   * The build, one slice per `next()`. A deferred view (the next island, built in the background
   * while the posse fights: client/render/warm.ts) is stepped by the frame budget and shown with
   * attach(); otherwise the constructor runs it straight through and attaches.
   */
  readonly building: Generator<unknown, void, unknown>;
  built = false;
  attached = false;
  constructor(ctx: ViewCtx, opts: { deferred?: boolean } = {}) {
    this.ctx = ctx;
    this.building = this.steps();
    if (!opts.deferred) { finish(this.building); this.attach(); }
  }

  private *steps(): Generator<unknown, void, unknown> {
    const ctx = this.ctx;
    // build against the day key: the backdrop bakes its valley and light from the sun direction,
    // which the cycle may have swung to the moon on the island before (re-keyed after every pause,
    // since the island on screen keeps writing its own mood between slices)
    setMood(0);
    const pause = function* (at: string) { budget.note = at; yield; setMood(0); };
    // the island as generated: destruction (sim/destruct.ts) lowers and parks collision on the live
    // world, so a view built after some of it has burnt (a late joiner) draws from a pristine copy
    const live = ctx.world;
    const world = live.dest.dirty ? new World(live.runSeed, live.index, live.arch) : live;
    const q = Math.max(0, Math.min(3, ctx.quality | 0));
    this.group.name = 'world';

    this.field = new GroundField(world, q >= 2 ? 0.5 : 0.75);
    yield* this.field.fillSteps(pause);
    const groundTex = this.field.texture();
    this.disposables.push(groundTex);

    // painted textures (landscape lane assets, all tiny and procedural or downscaled)
    // (shared across islands: loaded and uploaded once a session)
    const tex = (name: string, srgb: boolean) => sharedTexture(ASSET + name, `${q}${srgb}`, (t) => {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.anisotropy = q >= 2 ? 4 : 1;
      if (srgb) t.colorSpace = THREE.SRGBColorSpace;
    });
    const detail = tex('grass_detail.png', false);
    const cobble = tex('cobble.jpg', true);
    const rockDetail = tex('rock_detail.png', false);

    // islands: smooth painted tops + faceted strata underneath + the cobble path
    yield* pause('world:islands');
    const isl = yield* buildIslandsSteps(world, this.field, q, pause);
    yield* pause('world:tops');
    const topGeo = isl.top.build();
    yield* pause('world:strata');
    const underGeo = isl.under.build();
    const topMat = paintedMaterial({ detail, detailScale: 2.2, detailAmt: 0.42, cloudShadow: true, fogMax: 0.45 });
    const top = new THREE.Mesh(topGeo, topMat);
    top.receiveShadow = true;
    top.castShadow = false; // receive only: casting the whole plate doubles its cost in the shadow pass
    top.name = 'islandTops';
    const underMat = paintedMaterial({ detail: rockDetail, detailScale: 0.7, detailAmt: 0.62, triplanar: true, side: THREE.DoubleSide, fogMax: 0.18, rock: 'strata' });
    const under = new THREE.Mesh(underGeo, underMat);
    under.name = 'strata';
    const pathMat = paintedMaterial({ map: cobble, detail: rockDetail, detailScale: 5, detailAmt: 0.25 });
    const path = new THREE.Mesh(isl.path, pathMat);
    path.receiveShadow = true;
    path.name = 'path';
    this.group.add(top, under, path);
    this.disposables.push(topGeo, underGeo, topMat, underMat, isl.path, pathMat);

    // town (owned by town.ts)
    yield* pause('world:town');
    this.town = new Town(world, ctx, live);
    yield* this.town.steps(pause);
    this.group.add(this.town.group);

    // rocks, pillars, ruins, crates
    yield* pause('world:props');
    const propKit = buildProps(world);
    yield* pause('world:propsMesh');
    const propGeo = propKit.build();
    const propMat = paintedMaterial({ detail: rockDetail, detailScale: 1.1, detailAmt: 0.6, triplanar: true, rock: 'stone' });
    const props = new THREE.Mesh(propGeo, propMat);
    props.castShadow = q > 2; props.receiveShadow = true;
    props.name = 'props';
    const framerParts: THREE.BufferGeometry[] = [];
    yield* pause('world:rocks');
    const monoRuns = new Map<number, { start: number; count: number }>();
    const rockGeo = yield* buildRocksSteps(world, q, pause, framerParts, monoRuns);
    const rocks = new THREE.Mesh(rockGeo, propMat);
    rocks.castShadow = q > 1; rocks.receiveShadow = true;
    rocks.name = 'rocks';
    this.group.add(props, rocks);
    this.disposables.push(propGeo, propMat, rockGeo);
    if (framerParts.length) {
      // the big meadow boulders: bare, dark teal-grey framing masses (no moss caps)
      const frGeo = mergeAll(framerParts);
      const frMat = paintedMaterial({ detail: rockDetail, detailScale: 1.1, detailAmt: 0.5, triplanar: true });
      const fr = new THREE.Mesh(frGeo, frMat);
      fr.castShadow = q > 0; fr.receiveShadow = true;
      fr.name = 'framerRocks';
      this.group.add(fr);
      this.disposables.push(frGeo, frMat);
    }

    // landforms (spires, arches, floating shelves): strata rock with grassy crowns, like the islands
    yield* pause('world:landforms');
    const lfRuns: LandformRuns = new Map();
    const lf = buildLandforms(world, q, lfRuns);
    const lfRock = new THREE.Mesh(lf.rock, underMat);
    lfRock.castShadow = q > 0; lfRock.receiveShadow = true;
    lfRock.name = 'landforms';
    const lfTops = new THREE.Mesh(lf.tops, topMat);
    lfTops.receiveShadow = true;
    lfTops.name = 'landformTops';
    this.group.add(lfRock, lfTops);
    this.disposables.push(lf.rock, lf.tops);

    // stacked stone archways, and the standing stones and landforms once a blast fells them (world/stones.ts)
    yield* pause('world:stones');
    this.stones = new StonesView(live, q, propMat, rocks, monoRuns, { rock: lfRock, tops: lfTops, runs: lfRuns, mat: underMat, topMat });
    this.group.add(this.stones.group);

    // water: ponds, streams and the waterfalls off the edges
    yield* pause('world:water');
    this.water = new WaterView(world, q);
    this.group.add(this.water.group);
    this.disposables.push(this.water);

    // shrubs and trees (async: the converted tree meshes arrive a moment after the island)
    yield* pause('world:foliage');
    const leaves = tex('leaves.webp', true), bark = tex('bark.jpg', true);
    this.foliage = new Foliage(world, q, leaves, bark);
    this.group.add(this.foliage.group);

    // grass
    yield* pause('world:grass');
    const tuft = tex('tuft.png', false);
    this.grass = new Grass(this.field, groundTex, q, detail, tuft, leaves);
    for (const m of this.grass.meshes) this.group.add(m);

    // sky, cloud sea, silhouettes, motes
    yield* pause('world:sky');
    this.sky = new Sky(world.seed, q, ctx.renderer, this.dayNight);
    this.group.add(this.sky.group);
    // still water mirrors the land, the trees, the town and the sky (water.ts / reflection.ts)
    this.water.setReflectCasters([top, under, lfRock, lfTops, this.town.group, this.foliage.group, this.sky.group]);

    // warp gate
    yield* pause('world:gate');
    this.gate = new Gate(world, HAZE.uTime);
    this.group.add(this.gate.group);
    const bd = world.bonusDoor;
    if (bd) {
      this.bonusGate = new Gate(world, HAZE.uTime, { bonus: true, at: { x: bd.x, y: world.bonusDoorY(), z: bd.z } });
      this.group.add(this.bonusGate.group);
    }

    // the space train cruising far overhead (backdrop LOD)
    yield* pause('world:train');
    if (q >= 1) {
      this.train = new Train(world.seed, ctx);
      this.train.group.name = 'train';
      this.group.add(this.train.group);
    }

    this.built = true;
  }

  /** true once every part has arrived: the sliced build, the tree meshes, the door model, the train */
  get loaded() { return this.built && this.foliage.ready && this.gate.modelReady && (this.bonusGate?.modelReady ?? true) && (this.train?.loaded ?? true); }

  /** Show it: into the scene, lit by the stage's sun and sky. */
  attach() {
    if (this.attached) return;
    if (!this.built) finish(this.building);
    this.attached = true;
    const ctx = this.ctx;
    ctx.scene.add(this.group);
    ctx.scene.traverse((o) => {
      if (!this.sunLight && (o as THREE.DirectionalLight).isDirectionalLight) this.sunLight = o as THREE.DirectionalLight;
      if (!this.hemiLight && (o as THREE.HemisphereLight).isHemisphereLight) this.hemiLight = o as THREE.HemisphereLight;
    });
  }

  /** Seconds on the clock every client shares (server ticks / TICK_RATE): drives the day. */
  setDayClock(seconds: number) { this.dayNight.setClock(seconds); }

  /** Hand the post pass to the cycle (sun shafts follow the true sun, the grade cools at night). */
  bindPost(post: { material: THREE.ShaderMaterial; sunDir: THREE.Vector3 | null }) { this.dayNight.bindPost(post); }

  /** the true sun (the sky disc); the key light HAZE.uSunDir is the moon at night */
  get sunDisc() { return this.dayNight.sunDisc; }

  update(dt: number, time: number, camPos: THREE.Vector3, pushers: readonly { x: number; y: number; z: number; w?: number }[]): void {
    HAZE.uTime.value = time;
    // town mood: blue sky + thin haze inside the town footprint, golden meadow outside
    const t = this.ctx.world.town;
    const dx = Math.max(t.x0 - camPos.x, camPos.x - t.x1, 0), dz = Math.max(Math.abs(camPos.z - t.z) - 20, 0);
    const mood = smoothstep(34, 6, Math.hypot(dx, dz));
    // setMood writes the day (meadow/town) every frame, then the cycle blends from it toward the
    // time of day and places the key light (sun or moon) where nobody calls followShadow
    this.mood = mood; setMood(mood);
    this.dayNight.apply(this.sunLight, this.hemiLight);
    this.grass.update(this.ctx.camera, pushers);
    this.sky.update(camPos);
    this.gate.update(time, pushers, dt, camPos);
    this.bonusGate?.update(time, pushers, dt, camPos);
    this.train?.update(dt, time);
    this.town.update(dt, time, camPos);
    this.stones.update(dt);
    this.water.update(time, camPos);
  }

  /** Smoke, fire, dust and debris for burning buildings and falling stones come from the game's Fx. */
  bindFx(fx: RuinFx & StonesFx & { wallPaint?: (x: number, y: number, z: number, nx: number, nz: number) => readonly [number, number, number] | null }) {
    this.town.ruin.fx = fx;
    // a wall burst throws splinters in the paint of the wall it came out of
    fx.wallPaint = (x, y, z, nx, nz) => this.town.breachPaint(x, y, z, nx, nz);
    this.stones.fx = fx;
  }

  setGateCharge(v: number): void {
    this.gate.setCharge(v);
  }

  /** the saloon doors: lit and open once the island is cleared; warp 0..1 blows them wide */
  /** the bonus doors: open while the island's clear and the bonus hasn't been taken */
  setBonusGateState(open: boolean, warp: number): void {
    this.bonusGate?.setState(open, warp, 'HIGH STAKES · BIG PRIZES');
  }
  setGateState(open: boolean, warp: number, label: string): void {
    this.gate.setState(open, warp, label);
  }

  /** true once async landscape parts (foliage) are built; the lab waits on it before freezing */
  get landscapeReady() { return this.foliage.ready; }

  dispose(): void {
    // a background build dropped half way (the posse took the other door) has only some parts
    if (!this.built) this.building.return();
    this.ctx.scene.remove(this.group);
    this.grass?.dispose();
    this.sky?.dispose();
    this.gate?.dispose();
    this.bonusGate?.dispose();
    this.train?.dispose();
    this.town?.dispose();
    this.stones?.dispose();
    this.foliage?.dispose();
    for (const d of this.disposables) d.dispose();
  }
}
